import {
  Plus,
  X,
  MapPin,
  Search,
  Loader2,
} from 'lucide-react';
import type { DiversFormData, Project } from '../../hooks/useSchedulerData';
import { getEntityColors } from '../../hooks/useSchedulerData';

interface DiversModalProps {
  diversForm: DiversFormData;
  setDiversForm: React.Dispatch<React.SetStateAction<DiversFormData>>;
  diversGroups: Project[];
  isWagGroupSelected: boolean;
  isGeocoding: boolean;
  isSavingDivers: boolean;
  onClose: () => void;
  onAddressSearch: () => void;
  onCreate: () => void;
}

export function DiversModal({
  diversForm,
  setDiversForm,
  diversGroups,
  isWagGroupSelected,
  isGeocoding,
  isSavingDivers,
  onClose,
  onAddressSearch,
  onCreate,
}: DiversModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
        }}
      >
        {/* Header Modale */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: '#ecfdf5', padding: '8px', borderRadius: '8px' }}>
              <Plus size={20} color="#10b981" />
            </div>
            Nouveau Chantier Divers
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer'
            }}
          >
            <X size={20} color="#64748b" />
          </button>
        </div>

        {/* Formulaire */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Sélecteur de groupe Divers */}
          <div>
            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
              Groupe de rattachement <span style={{ color: '#ef4444' }}>*</span>
            </label>
            {diversGroups.length === 0 ? (
              <div style={{
                padding: '16px',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '10px',
                color: '#92400e',
                fontSize: '0.9rem'
              }}>
                ⚠️ Aucun groupe Divers disponible. Créez d'abord un groupe dans la page "Projets & Affaires".
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {diversGroups.map(group => {
                  const isSelected = diversForm.parent_project_id === group.id;
                  const ec = getEntityColors(group.parent_entity);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => setDiversForm({ ...diversForm, parent_project_id: group.id })}
                      style={{
                        flex: '1 1 calc(50% - 5px)',
                        minWidth: '140px',
                        padding: '14px',
                        borderRadius: '10px',
                        border: isSelected ? `2px solid ${ec.color}` : '1px solid #e2e8f0',
                        background: isSelected ? ec.bg : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left'
                      }}
                    >
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', color: isSelected ? ec.text : '#64748b' }}>
                        {group.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px', fontFamily: 'monospace' }}>
                        {group.code}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Numéro Divers */}
          <div>
            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
              N° Divers {!isWagGroupSelected && <span style={{ color: '#ef4444' }}>*</span>}
              {isWagGroupSelected && <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '0.8rem', marginLeft: '6px' }}>(optionnel)</span>}
            </label>
            <input
              type="text"
              value={diversForm.divers_number}
              onChange={(e) => setDiversForm({ ...diversForm, divers_number: e.target.value })}
              placeholder={isWagGroupSelected ? "Ex: W001 (optionnel)..." : "Ex: D001, D002..."}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'monospace',
                fontWeight: '600'
              }}
              autoFocus
            />
          </div>

          {/* Nom du chantier */}
          <div>
            <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
              Nom du chantier <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={diversForm.name}
              onChange={(e) => setDiversForm({ ...diversForm, name: e.target.value })}
              placeholder="Ex: Dépannage Client Martin"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Adresse avec géocodage */}
          <div>
            <label style={{ display: 'flex', fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '6px', alignItems: 'center', gap: '6px' }}>
              <MapPin size={16} /> Adresse (optionnel)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={diversForm.address}
                onChange={(e) => setDiversForm({ ...diversForm, address: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAddressSearch())}
                placeholder="Saisir l'adresse pour calculer la zone..."
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              />
              <button
                type="button"
                onClick={onAddressSearch}
                disabled={isGeocoding}
                title="Rechercher l'adresse"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#2563eb'
                }}
              >
                {isGeocoding ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
              Appuyez sur Entrée ou la loupe pour calculer la zone.
            </div>
          </div>

          {/* Affichage Zone calculée */}
          {diversForm.zone && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                background: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #22c55e'
              }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>ZONE</span>
                <div style={{ fontWeight: '800', fontSize: '1.4rem', color: '#16a34a' }}>
                  {diversForm.zone === 'GD' ? 'GD' : diversForm.zone}
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#166534' }}>
                Zone calculée automatiquement depuis le siège.
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '12px 20px',
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              color: '#475569'
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onCreate}
            disabled={isSavingDivers || (!isWagGroupSelected && !diversForm.divers_number.trim()) || !diversForm.name.trim()}
            style={{
              padding: '12px 24px',
              background: isSavingDivers || (!isWagGroupSelected && !diversForm.divers_number.trim()) || !diversForm.name.trim() ? '#94a3b8' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isSavingDivers || (!isWagGroupSelected && !diversForm.divers_number.trim()) || !diversForm.name.trim() ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isSavingDivers ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Création...
              </>
            ) : (
              <>
                <Plus size={18} /> Créer le chantier
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
